import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { $tk } from '@/_translate';

// Containers
const DefaultContainer = () => import('@/containers/DefaultContainer');

// Views
const Dashboard = () => import('@/views/coreui/Dashboard');

// Views - Pages
const Page404 = () => import('@/views/coreui/pages/Page404');
const Page500 = () => import('@/views/coreui/pages/Page500');
const Login = () => import('@/views/Pages/Login');
const Register = () => import('@/views/Pages/Register');
const PasswordForgot = () => import('@/views/Pages/PasswordReset/PasswordForgot');
const PasswordReset = () => import('@/views/Pages/PasswordReset/PasswordReset');

// Views - Administration
const Users = () => import('@/views/Administration/Users/Users');
const UserForm = () => import('@/views/Administration/Users/UserForm');
const Profile = () => import('@/views/Profile');
const Roles = () => import('@/views/Administration/Roles/Roles');
const RoleCreate = () => import('@/views/Administration/Roles/RoleCreate');
const RoleUpdate = () => import('@/views/Administration/Roles/RoleUpdate');
const AuditLogs = () => import('@/views/Administration/AuditLogs');
const ErrorReports = () => import('@/views/Administration/ErrorReports/ErrorReports');
const ErrorReport = () => import('@/views/Administration/ErrorReports/ErrorReport');
const ExtensionsList = () => import('@/views/Administration/Extensions/ExtensionsList.vue');
const ExtensionView = () => import('@/views/Administration/Extensions/ExtensionView');
const Notifications = () => import('@/views/Administration/Notifications/Notifications');

const Translations = () => import('@/views/Administration/Translations/Translations');
const TranslationForm = () => import('@/views/Administration/Translations/TranslationForm');

const Languages = () => import('@/views/Administration/Languages/Languages');
const LanguageForm = () => import('@/views/Administration/Languages/LanguageForm');

// GenCrud.0.stubpart (stub fragment will be generated bellow this line - do not delete this)
// GenCrud.0.TimeLogs.stubinsert.start
const TimeLogs = () => import('@/views/TimeLogs/TimeLogs');
const TimeLogForm = () => import('@/views/TimeLogs/TimeLogForm');
// GenCrud.0.TimeLogs.stubinsert.end

const Passwords = () => import('@/views/Passwords/Passwords');
const PasswordForm = () => import('@/views/Passwords/PasswordForm');
const Identities = () => import('@/views/Identities/Identities');
const IdentityForm = () => import('@/views/Identities/IdentityForm');

Vue.use(Router);

const publicRoutes = [
  'Login',
  'Register',
  'PasswordForgot',
  'PasswordReset',
];

const router = new Router({
  mode: 'hash', // https://router.vuejs.org/api/#mode
  linkActiveClass: 'open active',
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: '/pages',
      redirect: '/pages/404',
      name: null,
      component: {
        render(c) {
          return c('router-view');
        }
      },
      children: [
        {
          path: '404',
          name: 'Page404',
          meta: { label: $tk('route.error404') },
          component: Page404
        },
        {
          path: '500',
          name: 'Page500',
          meta: { label: $tk('route.error500') },
          component: Page500
        },
      ]
    },
    {
      path: '/',
      redirect: { name: 'Dashboard' },
      name: null,
      component: DefaultContainer,
      children: [
        // GenCrud.1.stubpart (stub fragment will be generated bellow this line - do not delete this)
        // GenCrud.1.TimeLogs.stubinsert.start
        {
          path: 'time_logs',
          name: null,
          meta: { label: $tk('route.time_logs_list_title') },
          redirect: { name: 'TimeLogs', params: { page: 1 } },

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page',
              name: 'TimeLogs',
              meta: { label: $tk('route.time_log_list') },
              props: true,
              component: TimeLogs,
            },
            {
              path: 'create',
              name: 'TimeLogCreate',
              meta: { label: $tk('route.time_log_create_title') },
              component: TimeLogForm
            },
            {
              path: 'update/:id',
              name: 'TimeLogUpdate',
              meta: { label: $tk('route.time_log_update_title') },
              props: (route) => {
                return {
                  id: Number(route.params.id)
                };
              },
              component: TimeLogForm
            },
          ]
        },
        // GenCrud.1.TimeLogs.stubinsert.end
        // GenCrud.1.Passwords.stubinsert.start
        {
          path: 'passwords',
          name: 'AllPasswords',
          meta: { label: $tk('route.passwords_list_title') },
          redirect: '/passwords/list/1',

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page?',
              name: 'Passwords',
              meta: { label: $tk('route.app_list') },
              props: (route) => {
                return {
                  page: Number(route.params.page || 1)
                };
              },
              component: Passwords,
            },
            {
              path: 'create',
              name: 'AppCreate',
              meta: { label: $tk('route.app_create_title') },
              component: PasswordForm
            },
            {
              path: 'update/:id',
              name: 'AppUpdate',
              meta: { label: $tk('route.app_update_title') },
              props: (route) => {
                return {
                  id: Number(route.params.id)
                };
              },
              component: PasswordForm
            },
          ]
        },
        // GenCrud.1.Passwords.stubinsert.end
        // GenCrud.1.Identities.stubinsert.start
        {
          path: 'identities',
          name: 'AllIdentities',
          meta: { label: $tk('route.identities_list_title') },
          redirect: '/identities/list/1',

          component: {
            render(c) { return c('router-view'); }
          },

          children: [
            {
              path: 'list/:page?',
              name: 'Identities',
              meta: { label: $tk('route.identity_list') },
              props: (route) => {
                return {
                  page: Number(route.params.page || 1)
                };
              },
              component: Identities,
            },
            {
              path: 'create',
              name: 'IdentityCreate',
              meta: { label: $tk('route.identity_create_title') },
              component: IdentityForm
            },
            {
              path: 'update/:id',
              name: 'IdentityUpdate',
              meta: { label: $tk('route.identity_update_title') },
              props: (route) => {
                return {
                  id: Number(route.params.id)
                };
              },
              component: IdentityForm
            },
          ]
        },
        // GenCrud.1.Identities.stubinsert.end

        {
          path: 'dashboard',
          name: 'Dashboard',
          meta: { label: $tk('route.dashboard') },
          component: Dashboard
        },
        // ------- Administration----------------

        {
          path: '/administration',
          name: 'Administration',
          meta: { label: $tk('route.administration') },
          redirect: { name: 'Users', params: { page: 1 } },
          component: {
            render(c) { return c('router-view'); }
          },
          children: [
            {
              path: 'users',
              name: 'AllUsers',
              meta: { label: $tk('route.users_list_title') },
              redirect: { name: 'Users', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page?',
                  name: 'Users',
                  meta: { label: $tk('route.users_list') },
                  props: true,
                  component: Users,
                },
                {
                  path: 'create',
                  name: 'UserCreate',
                  meta: { label: $tk('route.user_create_title') },
                  component: UserForm
                },
                {
                  path: 'update/:id',
                  name: 'UserUpdate',
                  meta: { label: $tk('route.user_update_title') },
                  props: true,
                  component: UserForm
                },
              ]
            },
            {
              path: 'roles',
              name: 'AllRoles',
              meta: { label: $tk('route.roles_list_title') },
              redirect: { name: 'Roles', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Roles',
                  meta: { label: $tk('route.role_list') },
                  props: true,
                  component: Roles,
                },
                {
                  path: 'create',
                  name: 'RoleCreate',
                  meta: { label: $tk('route.roles_create_title') },
                  component: RoleCreate
                },
                {
                  path: 'update/:id',
                  name: 'RoleUpdate',
                  meta: { label: $tk('route.role_update_title') },
                  props: true,
                  component: RoleUpdate
                },
              ]
            },
            {
              path: 'audit-logs',
              name: 'AllAuditLogs',
              meta: { label: $tk('route.audit_logs_list_title') },
              redirect: { name: 'AuditLogs', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'AuditLogs',
                  meta: { label: $tk('route.auditlogs_list') },
                  props: true,
                  component: AuditLogs,
                },
              ]
            },
            {
              path: 'extensions',
              meta: { label: $tk('route.extensions.title') },
              name: 'AllExtensions',
              redirect: { name: 'ExtensionsList', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'ExtensionsList',
                  meta: { label: $tk('route.extensions.list') },
                  props: true,
                  component: ExtensionsList,
                },
                {
                  path: 'view/:id',
                  name: 'ExtensionView',
                  meta: { label: $tk('route.extensions.view') },
                  props: true,
                  component: ExtensionView,
                },
              ],
            },
            {
              path: 'error-reports',
              name: 'AllErrorReports',
              meta: { label: $tk('route.error_reports.title') },
              redirect: { name: 'ErrorReports', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'ErrorReports',
                  meta: { label: $tk('route.error_reports.list') },
                  props: true,
                  component: ErrorReports,
                },
                {
                  path: 'view/:id',
                  name: 'ErrorReportView',
                  meta: { label: $tk('route.error_reports.view') },
                  props: true,
                  component: ErrorReport,
                },
              ],
            },
            {
              path: 'translations',
              name: 'AllTranslations',
              meta: { label: $tk('route.translations_list_title') },
              redirect: { name: 'Translations', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Translations',
                  meta: { label: $tk('route.translations_list') },
                  props: true,
                  component: Translations,
                },
                {
                  path: 'create',
                  name: 'TranslationCreate',
                  meta: { label: $tk('route.translation_create_title') },
                  component: TranslationForm
                },
                {
                  path: 'update/:id',
                  name: 'TranslationUpdate',
                  meta: { label: $tk('route.translation_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: TranslationForm
                },
              ]
            },

            {
              path: 'languages',
              name: 'AllLanguages',
              meta: { label: $tk('route.languages_list_title') },
              redirect: { name: 'Languages', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Languages',
                  meta: { label: $tk('route.languages_list') },
                  props: true,
                  component: Languages,
                },
                {
                  path: 'create',
                  name: 'LanguageCreate',
                  meta: { label: $tk('route.language_create_title') },
                  component: LanguageForm
                },
                {
                  path: 'update/:id',
                  name: 'LanguageUpdate',
                  meta: { label: $tk('route.language_update_title') },
                  props: (route) => {
                    return {
                      id: Number(route.params.id)
                    };
                  },
                  component: LanguageForm
                },
              ]
            },

            {
              path: 'notifications',
              name: 'AllNotifications',
              meta: { label: $tk('route.notifications_list_title') },
              redirect: { name: 'Notifications', params: { page: 1 } },

              component: {
                render(c) { return c('router-view'); }
              },

              children: [
                {
                  path: 'list/:page',
                  name: 'Notifications',
                  meta: { label: $tk('route.notifications_list') },
                  props: true,
                  component: Notifications,
                },
              ]
            },
          ]
        },

        {
          path: 'profile',
          name: 'Profile',
          meta: { label: $tk('route.profile_title') },
          component: Profile
        },

      ]
    },
    {
      path: '/login',
      name: 'Login',
      meta: { label: $tk('route.login') },
      component: Login
    },
    {
      path: '/register',
      name: 'Register',
      meta: { label: $tk('route.register') },
      component: Register
    },
    {
      path: '/password-forgot',
      name: 'PasswordForgot',
      meta: { label: $tk('route.forgot_password') },
      component: PasswordForgot
    },
    {
      path: '/password-reset/:token',
      props: true,
      name: 'PasswordReset',
      meta: { label: $tk('route.password_reset') },
      component: PasswordReset
    },
  ]
});

router.beforeEach((to, from, next) => {
  if (!publicRoutes.includes(to.name) && !store.getters['Auth/isLoggedIn']) {
    next({ name: 'Login' });
  }
  if (to.name === 'Login' && from.name && from.name !== 'Login'
    && from.name !== 'Register'
    && from.name !== 'PasswordForgot'
    && from.name !== 'PasswordReset') {
    sessionStorage.setItem('redirectPath', from.path);
  }

  next();
});

export default router;
